import { useEffect, useRef } from "react";
import { Skeleton } from "../primitives/Skeleton.js";
import { COLORS, RADIUS, SPACE, TYPE } from "../theme/index.js";
import { ToolCallCard } from "./ToolCallCard.js";
import type { ChatUIMessage } from "./types.js";

// Inline mention highlight — matches jared/russ/richard at word boundary
const KNOWN_MENTION_RE = /(^|\s)@(jared|russ|richard)/i;

function renderWithMention(content: string): React.ReactNode {
  const m = KNOWN_MENTION_RE.exec(content);
  if (!m || m.index === undefined) return content;
  const leadLen = m[1]?.length ?? 0;
  const matchStart = m.index + leadLen;
  const raw = `@${m[2] ?? ""}`;
  const matchEnd = matchStart + raw.length;
  return (
    <>
      {content.slice(0, matchStart)}
      <strong data-testid="mention-highlight" style={{ color: COLORS.brand }}>
        {raw}
      </strong>
      {content.slice(matchEnd)}
    </>
  );
}

interface ChatMessageListProps {
  messages: ChatUIMessage[];
  streaming: boolean;
  suggestedPrompts?: string[];
  onSendPrompt?: (prompt: string) => void;
}

export function ChatMessageList({
  messages,
  streaming,
  suggestedPrompts,
  onSendPrompt,
}: ChatMessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: messages length triggers scroll; bottomRef is stable
  useEffect(() => {
    if (typeof bottomRef.current?.scrollIntoView === "function") {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length, streaming]);

  const isEmpty = messages.length === 0;

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: `${SPACE[3]}px ${SPACE[4]}px`,
        display: "flex",
        flexDirection: "column",
        gap: SPACE[3],
      }}
    >
      {isEmpty && (
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: SPACE[3],
            paddingTop: SPACE[8],
          }}
        >
          <div
            style={{
              fontSize: TYPE.size.sm,
              color: COLORS.textDim,
              textAlign: "center",
            }}
          >
            Ask me anything about your pipeline, deals, or infrastructure.
          </div>

          {suggestedPrompts && suggestedPrompts.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: SPACE[2],
                width: "100%",
              }}
            >
              {suggestedPrompts.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => onSendPrompt?.(prompt)}
                  style={{
                    background: COLORS.surface,
                    border: `1px solid ${COLORS.border}`,
                    borderRadius: RADIUS.md,
                    padding: `${SPACE[2]}px ${SPACE[3]}px`,
                    fontSize: TYPE.size.sm,
                    color: COLORS.textMuted,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "border-color 120ms ease",
                  }}
                >
                  {prompt}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {messages.map((msg) => {
        if (msg.role === "system") return null;

        if (msg.role === "tool") {
          const toolData = (() => {
            try {
              return JSON.parse(msg.content) as {
                name?: string;
                args?: Record<string, unknown>;
                result?: string;
              };
            } catch {
              return { name: "tool", result: msg.content };
            }
          })();
          return (
            <ToolCallCard
              key={msg.id}
              toolName={toolData.name ?? "tool"}
              args={toolData.args}
              result={toolData.result}
            />
          );
        }

        const isUser = msg.role === "user";

        return (
          <div
            key={msg.id}
            data-testid={isUser ? "chat-message-user" : "chat-message-assistant"}
            style={{
              display: "flex",
              justifyContent: isUser ? "flex-end" : "flex-start",
            }}
          >
            {msg.streaming && !msg.content ? (
              <div
                style={{ width: "80%", display: "flex", flexDirection: "column", gap: SPACE[1] }}
              >
                <Skeleton width="100%" height={14} />
                <Skeleton width="70%" height={14} />
              </div>
            ) : (
              <div
                style={{
                  maxWidth: "85%",
                  padding: `${SPACE[2]}px ${SPACE[3]}px`,
                  borderRadius: isUser
                    ? `${RADIUS.xl}px ${RADIUS.xl}px ${RADIUS.sm}px ${RADIUS.xl}px`
                    : `${RADIUS.xl}px ${RADIUS.xl}px ${RADIUS.xl}px ${RADIUS.sm}px`,
                  fontSize: TYPE.size.sm,
                  lineHeight: 1.6,
                  ...(isUser
                    ? { background: COLORS.brand, color: "#fff" }
                    : {
                        background: COLORS.surface,
                        color: COLORS.text,
                        border: `1px solid ${COLORS.border}`,
                      }),
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "inherit",
                    fontSize: "inherit",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {isUser ? renderWithMention(msg.content) : msg.content}
                  {msg.streaming && (
                    <span
                      style={{
                        display: "inline-block",
                        width: 2,
                        height: "1em",
                        background: "currentColor",
                        opacity: 0.7,
                        marginLeft: 1,
                        animation: "blink 1s step-end infinite",
                      }}
                    />
                  )}
                </pre>
              </div>
            )}
          </div>
        );
      })}

      <div ref={bottomRef} />
    </div>
  );
}
